import {
  Box,
  Typography,
  Button,
  useMediaQuery,
  Container,
} from "@mui/material";
import { useRef, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation, Trans } from "react-i18next";
import SurveyQuestion from "../shared/SurveyQuestion";
import { getPostChatChoices } from "../../postChatChoices";
import { useForm, useWatch } from "react-hook-form";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import CheckmarkIcon from "../icons/CheckmarkIcon";
import SessionInvalidPage from "../shared/SessionInvalidPage";
import AlreadySubmittedPage from "../shared/AlreadySubmittedPage";
import SubmissionErrorBanner from "../shared/SubmissionErrorBanner";

import axios from "axios";
import axiosRetry from "axios-retry";
import * as Sentry from "@sentry/react";

import {
  serializePostChatSurvey,
  normalizeLanguage,
} from "../../services/Utils";

import useSurveyValidation from "../../hooks/useSurveyValidation";

axiosRetry(axios, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) =>
    axiosRetry.isNetworkError(error) ||
    axiosRetry.isRetryableError(error) ||
    (error.response &&
      error.response.status >= 500 &&
      error.response.status < 600),
});

const SurveyForm = ({ modality, interactionIdKey, filteredParams }) => {
  const pageLoadAt = useMemo(() => new Date(), []);
  const { t, i18n } = useTranslation("postChat");
  const [view, setView] = useState("form");
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const navigate = useNavigate();
  const { chatId } = useParams();

  const [searchParams] = useSearchParams();
  const lang = searchParams.get("lang");

  const query = lang === "es" ? "?lang=es" : "";

  const {
    howAreYouFeelingChoices,
    helpExtentChoices,
    stopSuicideChoices,
    counselorConcernedChoices,
    counselorUnderstoodChoices,
    plansAfterChoices,
    likelyToFollowPlansChoices,
    whatPreventedPlansChoices,
    raceIdentityChoices,
    militaryServiceChoices,
  } = getPostChatChoices(t);

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm({
    defaultValues: {
      howAreYouFeeling: "",
      helpExtent: "",
      stopSuicide: "",
      counselorConcerned: "",
      counselorUnderstood: "",
      plansAfter: "",
      likelyToFollowPlans: "",
      whatPreventedPlans: "",
      raceIdentity: "",
      raceIdentity_unlisted: "",
      militaryService: "",
      page_load_timestamp: pageLoadAt.toISOString().slice(0, 19) + "Z",
    },
    shouldUnregister: true,
  });

  const howAreYouFeelingRef = useRef(null);
  const helpExtentRef = useRef(null);
  const stopSuicideRef = useRef(null);
  const counselorConcernedRef = useRef(null);
  const counselorUnderstoodRef = useRef(null);
  const plansAfterRef = useRef(null);
  const likelyToFollowPlansRef = useRef(null);
  const whatPreventedPlansRef = useRef(null);
  const raceIdentityRef = useRef(null);
  const militaryServiceRef = useRef(null);

  const plansAfter = useWatch({ control, name: "plansAfter" });

  const validationPayload = useMemo(
    () => ({
      type_param: modality,
      session_identifier: interactionIdKey,
      ...(filteredParams?.caseID && { caseID: filteredParams.caseID }),
    }),
    [modality, interactionIdKey, filteredParams?.caseID],
  );

  // ToDo change to use routes so we can in track analytics
  const validationStatus = useSurveyValidation({
    endpoint: import.meta.env.VITE_LIFELINE_POSTCHAT_SURVEY_VALIDATE_API_URL,
    payload: validationPayload,
    componentName: "PostChatSurveyForm",
    interactionIdKey,
    initialStatus: "valid",
  });

  const submitPostChat = async (raw) => {
    const submissionEndpoint = import.meta.env
      .VITE_LIFELINE_POSTCHAT_SURVEY_API_URL;
    if (!submissionEndpoint) return;

    let cleaned = {};
    for (const [key, value] of Object.entries(raw)) {
      if (!(key.endsWith("_unlisted") && value === undefined)) {
        cleaned[key] = value;
      }
    }
    // Filter out unnecessary fields ending with "_unlisted" if their value is undefined
    cleaned = {
      ...cleaned,
      modality,
      interactionIdKey,
      locale: normalizeLanguage(i18n.language),
      caseID: filteredParams?.caseID,
      page_load_timestamp:
        cleaned.page_load_timestamp ||
        pageLoadAt.toISOString().slice(0, 19) + "Z",
    };

    const submissionPayload = serializePostChatSurvey(cleaned);

    try {
      await axios.post(submissionEndpoint, submissionPayload, {
        timeout: 30000,
      });
      return true;
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: "PostChatSurveyForm",
          action: "form_submission",
        },
        extra: {
          statusCode: error.response?.status,
          responseData: error.response?.data,
        },
        level: "error",
      });
      setView("internalServerError");
      return false;
    }
  };

  const onSubmit = async (data) => {
    const success = await submitPostChat(data);
    if (!success) return;

    const destination =
      data.stopSuicide === "exacerbated_condition"
        ? `/feedback/${chatId}/follow-up${query}`
        : `/feedback/${chatId}/thank-you${query}`;
    navigate(destination);
  };

  if (validationStatus === "sessionInvalid") return <SessionInvalidPage />;
  if (validationStatus === "alreadySubmitted") return <AlreadySubmittedPage />;

  return (
    <Container
      maxWidth="md"
      disableGutters
      sx={{ bgcolor: theme.palette.white, borderRadius: "10px" }}
    >
      {view === "internalServerError" && (
        <SubmissionErrorBanner translationKey="postChatSubmissionError" />
      )}
      <Container
        maxWidth="sm"
        disableGutters
        sx={{
          mt: isDesktop ? 6 : 2,
          pt: isDesktop ? 1 : 0,
          mb: isDesktop ? 13 : 0,
          pb: 1.5,
          px: 1.5,
          bgcolor: "transparent",
        }}
      >
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Typography
            variant="h2"
            sx={{
              fontSize: "25px",
              mt: isDesktop ? 0 : 4,
              textAlign: "center",
            }}
          >
            {t("headerText.tellUsYourExperience")}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              my: 1,
              mb: 1,
              textAlign: "center",
              lineHeight: isDesktop ? "22px" : "30px",
            }}
            component="div"
          >
            <Trans
              t={t}
              i18nKey="headerText.skipQuestionText"
              components={{
                emphasize: (
                  <span
                    style={{
                      fontWeight: "bold",
                      color: theme.palette.primary.main,
                    }}
                  />
                ),
              }}
            />
          </Typography>

          <Box ref={howAreYouFeelingRef}>
            <SurveyQuestion
              errors={errors}
              question={t("howAreYouFeelingQuestion.title")}
              options={howAreYouFeelingChoices}
              name="howAreYouFeeling"
              control={control}
              spacingTop={1.5}
              spacingBottom={1.5}
              namespace="postChat"
            />
          </Box>

          <Box ref={helpExtentRef}>
            <SurveyQuestion
              errors={errors}
              question={t("helpExtentQuestion.title")}
              options={helpExtentChoices}
              name="helpExtent"
              control={control}
              spacingTop={1.5}
              spacingBottom={1.5}
              namespace="postChat"
            />
          </Box>

          <Box ref={stopSuicideRef}>
            <SurveyQuestion
              errors={errors}
              question={t("stopSuicideQuestion.title")}
              options={stopSuicideChoices}
              name="stopSuicide"
              control={control}
              spacingTop={1.5}
              spacingBottom={1.5}
              namespace="postChat"
            />
          </Box>

          <Typography
            variant="h4"
            sx={{ fontSize: "20px", my: 2, color: theme.palette.primary.main }}
          >
            {t("formText.crisisCounselorQuestion")}
          </Typography>

          <Box ref={counselorConcernedRef}>
            <SurveyQuestion
              errors={errors}
              question={t("counselorConcernedQuestion.title")}
              options={counselorConcernedChoices}
              name="counselorConcerned"
              control={control}
              spacingTop={1.5}
              spacingBottom={1.5}
              namespace="postChat"
            />
          </Box>

          <Box ref={counselorUnderstoodRef}>
            <SurveyQuestion
              errors={errors}
              question={t("counselorUnderstoodQuestion.title")}
              options={counselorUnderstoodChoices}
              name="counselorUnderstood"
              control={control}
              spacingTop={1.5}
              spacingBottom={1.5}
              namespace="postChat"
            />
          </Box>

          <Box ref={plansAfterRef}>
            <SurveyQuestion
              errors={errors}
              question={t("plansAfterQuestion.title")}
              options={plansAfterChoices}
              name="plansAfter"
              control={control}
              spacingTop={1.5}
              spacingBottom={1.5}
              namespace="postChat"
            />
          </Box>

          {plansAfter === "yes" && (
            <Box ref={likelyToFollowPlansRef}>
              <SurveyQuestion
                errors={errors}
                question={t("likelyToFollowPlansQuestion.title")}
                options={likelyToFollowPlansChoices}
                name="likelyToFollowPlans"
                control={control}
                spacingTop={1.5}
                spacingBottom={1.5}
                namespace="postChat"
              />
            </Box>
          )}

          {plansAfter === "no" && (
            <Box ref={whatPreventedPlansRef}>
              <SurveyQuestion
                errors={errors}
                question={t("whatPreventedPlansQuestion.title")}
                options={whatPreventedPlansChoices}
                name="whatPreventedPlans"
                control={control}
                spacingTop={1.5}
                spacingBottom={1.5}
                namespace="postChat"
              />
            </Box>
          )}
          <Box ref={raceIdentityRef}>
            <SurveyQuestion
              errors={errors}
              question={t("raceIdentityQuestion.title")}
              options={raceIdentityChoices}
              name="raceIdentity"
              control={control}
              spacingTop={1.5}
              spacingBottom={1.5}
              namespace="postChat"
              maxCharacters={50}
            />
          </Box>

          <Box ref={militaryServiceRef}>
            <SurveyQuestion
              errors={errors}
              question={t("militaryServiceQuestion.title")}
              options={militaryServiceChoices}
              name="militaryService"
              control={control}
              spacingTop={1.5}
              spacingBottom={1.5}
              namespace="postChat"
            />
          </Box>

          <Button
            type="submit"
            disableRipple
            disabled={isSubmitting}
            sx={{
              mt: 3,
              mb: 2,
              height: 60,
              borderRadius: 50,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "5px",
              px: 3,
              mx: "auto",
              fontFamily: "Public Sans",
              fontWeight: "bold",
              fontSize: 22,
              bgcolor: theme.palette.primary.main,
              textTransform: "none",
              color: theme.palette.white,
              ":hover": { bgcolor: theme.palette.secondary.dark },
              ":focus": {
                bgcolor: theme.palette.secondary.dark,
                outline: `1px dashed ${theme.palette.tertiary.main}`,
                outlineOffset: 4,
              },
            }}
          >
            <CheckmarkIcon
              sx={{
                "& path": { fill: theme.palette.common.white },
                fontSize: "40px",
                verticalAlign: "middle",
                paddingTop: "5px",
              }}
            />
            {t("formText.submit")}
          </Button>
        </Box>
      </Container>
    </Container>
  );
};

SurveyForm.propTypes = {
  modality: PropTypes.string,
  interactionIdKey: PropTypes.string,
  filteredParams: PropTypes.object,
};

export default SurveyForm;
